import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCog,
  faRotateLeft,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import { LogoMarker_VM } from "../../design-editor2/components/logo-placer/marker.component";
import { LogoPlacementPosition } from "../../design-editor/models/Placement";
import { LogoImage } from "../../../components/Logo/LogoImage";
import { useExternalDesignContext } from "../contexts/external-design.context";
import { Direction } from "../../design-editor/design-editor-tool/DesignEditorTool.vm";

export function ExternalLogoMarker({
  marker,
  logoPlacements,
  isLocked,
  isActive,
  markerRef,
  onSelect,
}: LogoMarker_VM) {
  const {
    views,
    changeLogoView,
    rotateLogo,
    logosOfDesign,
    containerRef,
    config,
  } = useExternalDesignContext();
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const accentColor = config?.theme?.accentColor || "#2563eb"; // blue-600 default

  // Close settings when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        settingsRef.current &&
        !settingsRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowSettings(false);
      }
    }

    if (showSettings) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSettings]);

  function getLogoBorderColor(): string {
    switch (logoPlacements) {
      case LogoPlacementPosition.INVALID:
        return "border-red";
      case LogoPlacementPosition.OVERLAPPING:
        return "border-red";
      case LogoPlacementPosition.VALID:
        return "border-green";
      case LogoPlacementPosition.UNKNOWN:
        return "border-gray-600";
      default:
        return "border-gray-600";
    }
  }

  function buildLogoBorder(): string {
    let borderAttributes = "border";
    if (isLocked) {
      if (isActive) {
        borderAttributes += " border-solid border-1 border-blue";
      } else {
        borderAttributes += " border-solid border-1 border-transparent";
      }
    } else {
      borderAttributes += " " + getLogoBorderColor();

      if (isActive) {
        borderAttributes += " border-solid border-1";
      } else {
        borderAttributes += " border-dashed border-1";
      }
    }

    return borderAttributes;
  }

  function buildLogoBg(): string {
    return logoPlacements === LogoPlacementPosition.VALID
      ? "bg-[#7BBB5840]"
      : "bg-[#C6353540]";
  }

  function handleRotate(direction: Direction) {
    const designLogo = logosOfDesign.find((l) => l.inDesignId === marker.id);
    if (designLogo) {
      rotateLogo(designLogo, direction);
    }
  }

  // Calculate position for the portal
  const [dialogPosition, setDialogPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (showSettings && buttonRef.current && containerRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      // Calculate position relative to the container
      setDialogPosition({
        top: buttonRect.bottom - containerRect.top + 5, // 5px offset
        left: buttonRect.left - containerRect.left,
      });
    }
    // Only calculate when opening
  }, [showSettings]); // Removed marker dependencies to keep dialog fixed after opening

  return (
    <div
      id={`box-${marker.id}`}
      ref={markerRef}
      className={`box box-border rounded absolute z-[2] ${buildLogoBorder()} ${buildLogoBg()} ${
        isActive ? (isLocked ? "" : "cursor-move") : "cursor-pointer"
      }`}
      onClick={(x) => onSelect()}
    >
      <div className="relative w-full h-full">
        <button
          ref={buttonRef}
          type="button"
          className="absolute -top-2 -right-2 w-5 h-5 bg-white border border-gray-300 rounded-full flex items-center justify-center text-gray-600 transition-colors z-50 shadow-sm"
          style={{
            color: isHovered || showSettings ? accentColor : undefined,
            borderColor: isHovered || showSettings ? accentColor : undefined,
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          // We don't counter-rotate the button itself, it rotates with the logo which is expected/acceptable for the button
          onClick={(event) => {
            event.stopPropagation();
            setShowSettings(!showSettings);
          }}
          // Prevent dragging when clicking the button
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          aria-label="Logo settings"
        >
          <FontAwesomeIcon icon={faCog} className="text-[10px]" />
        </button>

        {showSettings &&
          containerRef.current &&
          ReactDOM.createPortal(
            <div
              ref={settingsRef}
              className="absolute w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-hidden cursor-default"
              style={{
                top: dialogPosition.top,
                left: dialogPosition.left,
                // No rotation here, so it stays upright!
              }}
              onClick={(e) => e.stopPropagation()}
              // Prevent dragging the logo when interacting with the menu
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              {/* Rotation Controls */}
              <div className="text-xs font-semibold text-gray-500 bg-gray-50 px-3 py-2 border-b border-gray-100">
                Rotation
              </div>
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-white">
                <button
                  type="button"
                  className="flex items-center justify-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRotate(Direction.Left);
                  }}
                  title="Rotate Left 15°"
                >
                  <FontAwesomeIcon icon={faRotateLeft} className="mr-1.5" />
                  -15°
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRotate(Direction.Right);
                  }}
                  title="Rotate Right 15°"
                >
                  +15°
                  <FontAwesomeIcon icon={faRotateRight} className="ml-1.5" />
                </button>
              </div>

              <div className="text-xs font-semibold text-gray-500 bg-gray-50 px-3 py-2 border-b border-gray-100">
                Move to view
              </div>
              <div className="max-h-40 overflow-y-auto bg-white">
                {views.map((view) => (
                  <button
                    key={view.id}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      view.id === marker.viewId
                        ? "font-medium bg-gray-50"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    style={{
                      color:
                        view.id === marker.viewId ? accentColor : undefined,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = accentColor;
                      e.currentTarget.style.backgroundColor = `${accentColor}10`; // 10% opacity
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color =
                        view.id === marker.viewId ? accentColor : "";
                      e.currentTarget.style.backgroundColor =
                        view.id === marker.viewId ? "#f9fafb" : "";
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      changeLogoView(marker.id, view.id);
                      setShowSettings(false);
                    }}
                  >
                    {view.view}
                  </button>
                ))}
              </div>
            </div>,
            containerRef.current // Render directly into the main container
          )}

        <LogoImage
          className="w-full h-full object-contain"
          useDigit={true}
          logo={marker.logo}
        />
      </div>
    </div>
  );
}
